/**
 * Title / attract screen (design 01 TITLE / PRESS START, `jQ0jL`): hype-ray sunburst, the HAND EGG logo
 * with the SUNDAY ARCADE EDITION ribbon, a flaming football, the four approved heads with their gloves, blinking
 * PRESS START, credits/hi-score corners. Any key, click, tap or pad button → Main Menu. After a few idle seconds
 * the attract loop alternates PRESS START with the heads' taglines in a broadcast lower-third (same slot, so the
 * card never covers the heads or their gloves).
 */
import './title.css';
import { useEffect, useRef, useState } from 'preact/hooks';
import { CHARACTERS, defaultMatchConfig, Difficulty, randomSeed, specialInfo } from '@hf/shared';
import { DEVICE_ID } from '@hf/client-core/input/types';
import { useActions, useServices } from '../actions';
import { Art, Glove, Head, Logo, Rays, Stroke } from '../components';
import { PublicInfo } from '../PublicInfo';
import { useMenuLayer, LAYER } from '../nav';
import { playSfx, UI_SFX } from '@hf/client-core/ui-model/sfx';
import { ui } from '@hf/client-core/ui-model/store';

const ATTRACT_LINES = [
  ...CHARACTERS.map((c) => ({ c: c.id, title: c.name, sub: `${c.role} · ${specialInfo(c.signature).name}` })),
  { c: null, title: 'HURDLE BEATS DIVE', sub: 'Leap beats Hurdle · Truck beats Leap · Dive beats Truck' },
  { c: null, title: 'EVERY MOVE HAS A FAKE', sub: 'Sell the jump, slam it down, truck right under him' },
];

const ATTRACT_SHOW_MS = 3200;
const ATTRACT_GAP_MS = 2600;

export function Title() {
  const actions = useActions();
  const services = useServices();
  const [leaving, setLeaving] = useState(false);
  const [attract, setAttract] = useState(-1);
  const started = useRef(false);

  const start = (): void => {
    if (started.current) return;
    started.current = true;
    setLeaving(true);
    actions.unlockAudio();
    playSfx(services.audio, UI_SFX.start);
    setTimeout(() => actions.goto('main_menu'), 380);
  };

  useMenuLayer(() => start(), true, LAYER.SCREEN);

  useEffect(() => {
    // Any key (not only the mapped menu keys) presses start.
    const onKey = (e: KeyboardEvent): void => {
      if (e.repeat || ['Shift', 'Control', 'Alt', 'Meta', 'F3', 'F5', 'F11', 'F12'].includes(e.key)) return;
      start();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Attract loop: after 6 s idle, a tagline card takes PRESS START's slot for 3.2 s, then PRESS START blinks
  // again for 2.6 s before the next card (the card never sits over the heads, gloves or logo).
  useEffect(() => {
    let i = -1;
    let t: ReturnType<typeof setTimeout>;
    const show = (): void => {
      i = (i + 1) % ATTRACT_LINES.length;
      setAttract(i);
      t = setTimeout(hide, ATTRACT_SHOW_MS);
    };
    const hide = (): void => {
      setAttract(-1);
      t = setTimeout(show, ATTRACT_GAP_MS);
    };
    t = setTimeout(show, 6000);
    return () => clearTimeout(t);
  }, []);

  /** Smoke-test / dev shortcut: straight into a vs-CPU match. */
  const quickStart = (e: Event): void => {
    e.stopPropagation();
    actions.unlockAudio();
    const name = ui.settings.value.playerName.trim() || 'P1';
    actions.startMatch({
      mode: 'cpu',
      config: defaultMatchConfig({ seed: randomSeed(), characters: ['rookie', 'bigtex'] }),
      slots: [
        {
          kind: 'human',
          name,
          character: 'rookie',
          deviceIds: [DEVICE_ID.KB_SOLO, DEVICE_ID.ANY_PAD, DEVICE_ID.TOUCH],
        },
        { kind: 'cpu', name: 'CPU', character: 'bigtex', difficulty: Difficulty.NORMAL },
      ],
    });
  };

  const line = attract >= 0 ? ATTRACT_LINES[attract] : null;
  const hi = 999_990;

  return (
    <div
      class={`hf-screen hf-title ${leaving ? 'leaving' : ''}`}
      data-screen="title"
      onPointerDown={(e) => {
        if ((e.target as HTMLElement).closest('[data-testid="start-cpu"], [data-testid="public-info"]'))
          return;
        start();
      }}
    >
      <Rays a="#e0287a" b="#1b0f3a" rays={22} x="50%" y="42%" speed={90} />
      <div class="hf-title__speed" aria-hidden="true" />
      <div class="hf-title__logo">
        <Logo edition />
        <div class="hf-title__ball" aria-hidden="true">
          <div class="hf-title__trail" />
          <Art
            keys={['prop/football', 'prop/ball']}
            class="hf-title__ballart"
            fallback={<div class="hf-title__ballfb" />}
          />
        </div>
      </div>

      <div class="hf-title__heads" aria-hidden="true">
        <div class="hf-title__rig r1">
          <Head character="grandpa" class="bob" />
          <Glove side="home" pose="ready" class="g" />
        </div>
        <div class="hf-title__rig r2">
          <Head character="bigtex" class="bob" />
          <Glove side="home" pose="ready" class="g" />
        </div>
        <div class="hf-title__rig r3 away">
          <Glove side="away" pose="ready" class="g" />
          <Head character="zorg" flip class="bob" />
        </div>
        <div class="hf-title__rig r4 away">
          <Glove side="away" pose="ready" class="g" />
          <Head character="rookie" flip class="bob" />
        </div>
      </div>

      <div class={`hf-title__press ${line ? 'yield' : ''}`}>
        <Stroke text="PRESS START" class="t-display" />
      </div>

      {line ? (
        <div class="hf-title__attract" key={attract} role="status">
          {line.c ? <Head character={line.c} expression="determined" class="hf-title__attract-head" /> : null}
          <div>
            <div class="t-display hf-title__attract-title">{line.title}</div>
            <div class="t-broadcast hf-title__attract-sub">{line.sub}</div>
          </div>
        </div>
      ) : null}

      <div class="hf-title__footer t-broadcast">
        <span class="credit">CREDIT 00</span>
        <span class="copy">© 2026 Amir Angel · 1–2 PLAYERS</span>
        <span class="hi">HI {hi.toLocaleString('en-US')}</span>
      </div>
      <PublicInfo />
      <button class="hf-chip dark hf-title__quick interactive" data-testid="start-cpu" onClick={quickStart}>
        ▶ QUICK PLAY VS CPU
      </button>
      <div class="hf-crt" aria-hidden="true" />
    </div>
  );
}
